import { datadogRum } from '@datadog/browser-rum';

import { AnalyticsDestination } from './types';
import { cioDestination } from './destinations/customerio';

const ENABLED_DESTINATIONS: Record<string, AnalyticsDestination> = { customerio: cioDestination };

const safeCall = (fn: () => void, error_context: Record<string, any> = {}) => {
  try {
    fn();
  } catch (error) {
    datadogRum.addError(error, {
      module: 'Analytics',
      ...error_context,
    });
  }
};

const analytics = {
  identify(id: string, traits: Record<string, any> = {}) {
    Object.entries(ENABLED_DESTINATIONS).forEach(([destinationName, destinationClass]) =>
      safeCall(() => destinationClass.identify(id, traits), {
        destination: destinationName,
        method: 'identify',
      }),
    );
  },

  identifyByEmail(email: string, traits: Record<string, any> = {}) {
    Object.entries(ENABLED_DESTINATIONS).forEach(([destinationName, destinationClass]) =>
      safeCall(() => destinationClass.identifyByEmail(email, traits), {
        destination: destinationName,
        function: 'identifyByEmail',
      }),
    );
  },

  track(event: string, data: Record<string, any> = {}) {
    Object.entries(ENABLED_DESTINATIONS).forEach(([destinationName, destinationClass]) =>
      safeCall(() => destinationClass.track(event, data), {
        destination: destinationName,
        method: 'track',
        event,
      }),
    );
  },

  trackCheckoutStarted(
    cart: Cart,
    subscription?: Record<string, any>,
    utmData?: UtmData,
    region?: Region,
  ) {
    Object.entries(ENABLED_DESTINATIONS).forEach(([destinationName, destinationClass]) =>
      safeCall(() => destinationClass.trackCheckoutStarted?.(cart, subscription, utmData, region), {
        destination: destinationName,
        method: 'trackCheckoutStarted',
        cart_id: cart?.id,
      }),
    );
  },

  trackRemoveFromCart(cart: Cart, removedKit: Kit, utmData?: UtmData, region?: Region) {
    Object.entries(ENABLED_DESTINATIONS).forEach(([destinationName, destinationClass]) =>
      safeCall(() => destinationClass.trackRemoveFromCart?.(cart, removedKit, utmData, region), {
        destination: destinationName,
        method: 'trackRemoveFromCart',
        cart_id: cart?.id,
        kit_id: removedKit?.kitId,
      }),
    );
  },

  page(name?: string, properties: Record<string, any> = {}) {
    Object.entries(ENABLED_DESTINATIONS).forEach(([destinationName, destinationClass]) =>
      safeCall(() => destinationClass.page?.(name, properties), {
        destination: destinationName,
        method: 'page',
        pageName: name,
      }),
    );
  },
};

export { analytics, ENABLED_DESTINATIONS };
